/**
 * Who a help topic is for. A topic lists one or more of these; a reader sees it
 * when any of them is theirs. The server applies the same rule
 * (HelpContentService) before anything reaches the browser -- this copy only
 * decides which edit controls and labels to show.
 *
 * - everyone: every signed-in person
 * - hr: HR, admins and super admins
 * - admin: admins and super admins
 * - super_admin: super admins only
 */
export type HelpRole = 'everyone' | 'hr' | 'admin' | 'super_admin';

export const HELP_ROLES: HelpRole[] = ['everyone', 'hr', 'admin', 'super_admin'];

/** The roles HR may give a topic, and the only topics HR may change. */
export const HR_EDITABLE_ROLES: HelpRole[] = ['everyone', 'hr'];

export function isSuperAdminUser(user: any): boolean {
  return user?.platformRole === 'super_admin' || user?.platform_role === 'super_admin';
}

/** Every role this person reads topics for. */
export function viewerHelpRoles(user: any): HelpRole[] {
  if (!user) return [];
  const superAdmin = isSuperAdminUser(user);
  const admin = superAdmin || !!user.isAdmin;
  const hr = admin || !!user.isHr;
  const roles: HelpRole[] = ['everyone'];
  if (hr) roles.push('hr');
  if (admin) roles.push('admin');
  if (superAdmin) roles.push('super_admin');
  return roles;
}

export function canRead(viewerRoles: HelpRole[], topicRoles: string[]): boolean {
  return topicRoles.some((r) => viewerRoles.includes(r as HelpRole));
}

/** The role a person is shown help and the welcome tour for: the highest they have. */
export function primaryHelpRole(user: any): 'member' | 'hr' | 'admin' {
  const roles = viewerHelpRoles(user);
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('hr')) return 'hr';
  return 'member';
}

/** Which roles this editor may put on a topic (HR: everyone and HR only). */
export function assignableRoles(user: any): HelpRole[] {
  const roles = viewerHelpRoles(user);
  if (roles.includes('admin')) return HELP_ROLES;
  if (roles.includes('hr')) return HR_EDITABLE_ROLES;
  return [];
}

/** The narrowest audience a topic names, for its "For HR" style label; null when it is for everyone. */
export function audienceLabelKey(topicRoles: string[]): string | null {
  if (topicRoles.includes('everyone')) return null;
  if (topicRoles.includes('hr')) return 'help.audience.hr';
  if (topicRoles.includes('admin')) return 'help.audience.admin';
  if (topicRoles.includes('super_admin')) return 'help.audience.superAdmin';
  return null;
}
